/* Localization pickers (snippets/localization.liquid).

   The component works with this file absent: the panel is a native [popover] that the UA opens
   from popovertarget (Escape and click-outside included), and every option is a real submit
   button in a {% form 'localization' %}. This module only adds the three things CSS and the
   platform cannot do on their own:

   1. Desktop placement in browsers without CSS anchor positioning. Where `position-area` is
      supported the panel is anchored in CSS and this file stays out of the way; elsewhere it
      writes --mw-loc-x / --mw-loc-y and adds .is-anchored — the same idea as Helix's
      --dropdown-dialog-shift-x, measured 20.09.
   2. aria-expanded on the trigger (UAs that do not derive it from popovertarget).
   3. Arrow / Home / End navigation between options, with focus moved into the panel on open.
      Tab still works, so the no-JS path is never worse.
   4. Opening on hover where the merchant asked for it (data-mw-loc-trigger="hover", which is
      Helix' default): pointer devices with a real hover only, and focus is left where it was —
      a panel that appears because a pointer passed over it must not take the keyboard with it.
      Clicking still opens and closes it, and Escape still closes it. */

const ANCHOR_IN_CSS = typeof CSS !== 'undefined' && CSS.supports && CSS.supports('position-area', 'block-end');
const desktop = window.matchMedia('(min-width: 768px)');
/* A phone reports no hover: there, «hover to open» would mean «open on the first tap and close
   on the second», which is what clicking already does. */
const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');

/* Leaving the trigger on the way to the panel passes over a few pixels of neither. The grace
   period is the width of that gap in time. */
const LEAVE_MS = 160;
const hoverTimers = new WeakMap();
const openedByHover = new WeakSet();

const GAP = 6;
const EDGE = 12;

let placed = null; // the panel currently positioned by this file

const trigger = (panel) => panel.closest('.mw-loc__picker')?.querySelector('.mw-loc__trigger') ?? null;
const options = (panel) => [...panel.querySelectorAll('.mw-loc__option')];

/* Helix hangs the panel under the whole header bar, not under the 16 px trigger inside it:
   measured 1440, their bar ends at 118 and their panel starts at 119. Ours started at 92, which
   put it inside the bar it belongs under. In the footer or the menu there is no bar to hang
   from, and the trigger itself is the anchor. */
/* The same distance, handed to CSS for the browsers that place the panel themselves. */
function writeDrop(picker) {
  const bar = picker.closest('.site-header__bar');
  const button = picker.querySelector('.mw-loc__trigger');
  if (!bar || !button) return;
  const drop = bar.getBoundingClientRect().bottom - button.getBoundingClientRect().bottom;
  picker.style.setProperty('--mw-loc-drop', `${Math.max(0, Math.round(drop)) + GAP}px`);
}

function writeAllDrops() {
  document.querySelectorAll('.mw-loc__picker').forEach(writeDrop);
}

function anchorOf(button) {
  const bar = button.closest('.site-header__bar');
  if (!bar) return button.getBoundingClientRect();
  const b = bar.getBoundingClientRect();
  const t = button.getBoundingClientRect();
  return { left: t.left, right: t.right, top: t.top, bottom: b.bottom };
}

function place(panel) {
  const button = trigger(panel);
  if (!button) return;
  const t = anchorOf(button);
  const style = getComputedStyle(panel);
  // Before the panel is shown its rect is empty; the computed width still resolves the min().
  const rect = panel.getBoundingClientRect();
  const width = rect.width || parseFloat(style.width) || 330;
  const height = rect.height || parseFloat(style.maxHeight) || 362;
  const alignEnd = style.getPropertyValue('--mw-loc-align').trim() === 'end';

  let x = alignEnd ? t.right - width : t.left;
  x = Math.min(Math.max(EDGE, x), Math.max(EDGE, window.innerWidth - width - EDGE));

  let y = t.bottom + GAP;
  if (y + height > window.innerHeight - EDGE) {
    const above = t.top - height - GAP;
    if (above > EDGE) y = above;
    else y = Math.max(EDGE, window.innerHeight - height - EDGE);
  }

  panel.style.setProperty('--mw-loc-x', `${Math.round(x)}px`);
  panel.style.setProperty('--mw-loc-y', `${Math.round(y)}px`);
}

function reposition() {
  if (placed?.isConnected && placed.matches(':popover-open')) place(placed);
  else placed = null;
}

// Non-bubbling events: capture on document still reaches them.
document.addEventListener(
  'beforetoggle',
  (event) => {
    const panel = event.target;
    if (!(panel instanceof HTMLElement) || !panel.matches('[data-mw-loc-panel]')) return;
    if (event.newState !== 'open' || ANCHOR_IN_CSS || !desktop.matches) return;
    // Placed before the panel is shown, so the first painted frame is already in position.
    panel.classList.add('is-anchored');
    place(panel);
  },
  true
);

document.addEventListener(
  'toggle',
  (event) => {
    const panel = event.target;
    if (!(panel instanceof HTMLElement) || !panel.matches('[data-mw-loc-panel]')) return;
    const open = event.newState === 'open';
    trigger(panel)?.setAttribute('aria-expanded', String(open));

    if (!open) {
      if (placed === panel) placed = null;
      panel.classList.remove('is-anchored');
      return;
    }

    if (!ANCHOR_IN_CSS && desktop.matches) {
      placed = panel;
      place(panel);
    }

    /* Opened by a pointer passing over it: leave the keyboard where the shopper left it. */
    if (openedByHover.has(panel)) return;

    const list = options(panel);
    (list.find((o) => o.getAttribute('aria-current') === 'true') ?? list[0])?.focus({ preventScroll: true });
  },
  true
);

/* ---------- Hover ---------- */

const panelOf = (picker) => picker.querySelector('[data-mw-loc-panel]');

function hoverOpen(picker) {
  clearTimeout(hoverTimers.get(picker));
  const panel = panelOf(picker);
  if (!panel || panel.matches(':popover-open')) return;
  openedByHover.add(panel);
  try {
    panel.showPopover();
  } catch {
    openedByHover.delete(panel);
  }
}

function hoverClose(picker) {
  clearTimeout(hoverTimers.get(picker));
  hoverTimers.set(
    picker,
    setTimeout(() => {
      const panel = panelOf(picker);
      if (!panel || !openedByHover.has(panel)) return;
      /* Someone tabbed into it after the pointer opened it: it is theirs now. */
      if (panel.contains(document.activeElement)) return;
      openedByHover.delete(panel);
      try {
        panel.hidePopover();
      } catch {
        /* Already closed. */
      }
    }, LEAVE_MS)
  );
}

document.addEventListener('pointerover', (event) => {
  if (!canHover.matches || !desktop.matches) return;
  const picker = event.target instanceof Element ? event.target.closest('[data-mw-loc-trigger="hover"]') : null;
  if (!picker) return;
  hoverOpen(picker);
});

document.addEventListener('pointerout', (event) => {
  const picker = event.target instanceof Element ? event.target.closest('[data-mw-loc-trigger="hover"]') : null;
  if (!picker) return;
  /* Moving between two children of the same picker is not leaving it. */
  if (event.relatedTarget instanceof Node && picker.contains(event.relatedTarget)) return;
  hoverClose(picker);
});

/* The panel is a popover, so the UA may render it outside the picker in the top layer: its own
   pointer events have to keep it open. */
document.addEventListener('pointerover', (event) => {
  const panel = event.target instanceof Element ? event.target.closest('[data-mw-loc-panel]') : null;
  const picker = panel?.closest('[data-mw-loc-trigger="hover"]');
  if (picker) clearTimeout(hoverTimers.get(picker));
});

/* A click on the trigger while hover already opened it should close it, not reopen it. */
document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('.mw-loc__trigger') : null;
  const panel = button ? panelOf(button.closest('.mw-loc__picker')) : null;
  if (panel) openedByHover.delete(panel);
});

document.addEventListener('keydown', (event) => {
  const option = event.target instanceof HTMLElement ? event.target.closest('.mw-loc__option') : null;
  if (!option) return;
  const panel = option.closest('[data-mw-loc-panel]');
  if (!panel) return;
  const list = options(panel);
  const index = list.indexOf(option);
  let next = -1;
  if (event.key === 'ArrowDown') next = (index + 1) % list.length;
  else if (event.key === 'ArrowUp') next = (index - 1 + list.length) % list.length;
  else if (event.key === 'Home') next = 0;
  else if (event.key === 'End') next = list.length - 1;
  if (next < 0) return;
  event.preventDefault();
  list[next].focus();
});

window.addEventListener('resize', () => {
  writeAllDrops();
  reposition();
});
window.addEventListener('scroll', reposition, { passive: true, capture: true });

// Theme editor: a section re-render drops the panel while it was open.
writeAllDrops();

document.addEventListener('shopify:section:load', () => {
  writeAllDrops();
  placed = null;
});
